/// <amd-module name="WorkItemsHub/Utils/WorkItemsHubReportUtils" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { RecentActivityConstants, MentionedTabDataProvider } from "Generated/Constants";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import * as Utils_Clipboard from "WorkItemsHub/Utils/Clipboard";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { wait } from "VSS/Core/Util/Promise";
import {
    WorkItemHtmlTableFormatter,
    IWorkItemHtmlTableFormatterColumn,
    WorkItemHtmlTableFormatType
} from "WorkItemsHub/Utils/WorkItemHtmlTableFormatter";
import { Callout, ICalloutProps } from "VSSUI/Callout";
import { IWorkItemsGridRow } from "DataContracts/IWorkItemsGridData";
import { ContextualMenuButton } from "VSSUI/ContextualMenuButton";
import { Location } from "VSSUI/Utilities/Position";
import * as Constants from "WorkItemsHub/Constants";
import * as Resources from "WorkItemsHub/Resources";

export class WorkItemsHubReportUtils {
    private static readonly ColumnsToExclude = [
        RecentActivityConstants.MyActivityDateField,
        RecentActivityConstants.MyActivityDetailsField,
        RecentActivityConstants.RecentlyUpdatedDateField,
        MentionedTabDataProvider.MentionedDateField
    ];

    private static readonly HtmlTableFormatterType: { [key: string]: WorkItemHtmlTableFormatType } = {
        [CoreFieldRefNames.ChangedDate]: WorkItemHtmlTableFormatType.Date,
        [CoreFieldRefNames.CreatedDate]: WorkItemHtmlTableFormatType.Date,
        [CoreFieldRefNames.AssignedTo]: WorkItemHtmlTableFormatType.Identity,
        [CoreFieldRefNames.CreatedBy]: WorkItemHtmlTableFormatType.Identity
    };

    /**
     * Copy the work items to clipboard in a table format
     * @param fieldReferenceNames Fields to include
     * @param fieldFriendlyNames The friendly names which corresponds to field reference names
     * @param fieldValues List of field values
     * @param ev Optional mouse event object (from command bar if clicked, or undefined from keyboard shortcut)
     * @param targetRow Target row
     */
    public static copyWorkItemsToClipboard(
        fieldReferenceNames: string[],
        fieldFriendlyNames: string[],
        fieldValues: any[][],
        pageContext: IVssPageContext,
        ev?: React.MouseEvent<HTMLElement>,
        targetRow?: IWorkItemsGridRow
    ): void {
        const columns = fieldReferenceNames.map(
            (fieldReferenceName, index) =>
                ({
                    index,
                    fieldReferenceName,
                    name: fieldFriendlyNames[index],
                    formatType: WorkItemsHubReportUtils.HtmlTableFormatterType[fieldReferenceName]
                } as IWorkItemHtmlTableFormatterColumn)
        );
        const filteredColumns = columns.filter(c => WorkItemsHubReportUtils.ColumnsToExclude.indexOf(c.fieldReferenceName) < 0);

        const table = new WorkItemHtmlTableFormatter(fieldValues, filteredColumns, pageContext).getHtml();
        Utils_Clipboard.copyToClipboard(pageContext, table, { copyAsHtml: true } as Utils_Clipboard.IClipboardOptions);
        if (ev) {
            WorkItemsHubReportUtils._showCopiedCallout(ev, targetRow);
        }
    }

    /**
     * Opens the dialog to email a table formatted list of work items
     * @param fieldReferenceNames Fields to include in the email
     * @param workItemIds IDs of work items to include in the email
     */
    public static openSendEmailDialog(pageContext: IVssPageContext, fieldReferenceNames: string[], workItemIds: number[], subject?: string): void {
        const filteredFields = fieldReferenceNames.filter(f => WorkItemsHubReportUtils.ColumnsToExclude.indexOf(f) < 0);
        const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(() => {
            return React.createElement(
                LegacyComponent,
                {
                    modules: ["WorkItemsHub/Scripts/LegacyPlatform/SendMailDialog"],
                    wrappedType: "sendMailDialog",
                    workItemIds: workItemIds,
                    fields: filteredFields,
                    subject: subject
                },
                null
            );
        });
    }

    private static _showCopiedCallout(e: React.MouseEvent<HTMLElement>, targetRow: IWorkItemsGridRow) {
        let container = document.createElement("div");
        document.body.appendChild(container);

        const closeCallout = () => {
            if (container) {
                ReactDOM.unmountComponentAtNode(container);
                container.parentElement.removeChild(container); // Note: IE11 doesn't support remove()
                container = null;
            }
        };

        const calloutProps: ICalloutProps = {
            anchorOrigin: { horizontal: Location.center, vertical: Location.end },
            calloutOrigin: { horizontal: Location.center, vertical: Location.start },
            onDismiss: closeCallout,
            contentClassName: "work-items-hub-callout"
        };

        // handle keyboard event (clientX or clientY is 0 or less)
        if (targetRow && !(e.clientX > 0 && e.clientY > 0)) {
            calloutProps.anchorElement = document.querySelector(
                `.${Constants.TabContent_WorkItemsHubGridRowClassName}[role="row"][data-item-key="${targetRow.key}"] .${
                    ContextualMenuButton.ButtonContainerClassName
                }`
            ) as HTMLElement;
        } else {
            calloutProps.anchorPoint = { x: e.clientX, y: e.clientY };
        }

        ReactDOM.render(<Callout {...calloutProps}>{Resources.CopiedContentDialogTitle}</Callout>, container);

        wait(500).then(closeCallout);
        Utils_Accessibility.announce(Resources.CopiedContentDialogTitle, true);
    }
}
